import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addMunicipality,
  deleteMunicipality,
  fetchMunicipalities,
  fetchMunicipalityDetails,
  updateMunicipality,
} from '../../common/api/municipalitiesService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface MunicipalityDetails {
  id?: string;
  isDeleted?: boolean;
  latitude: string;
  longitude: string;
  regionId: string;
  nameTranslations: TranslationItem[];
  descriptionTranslations: TranslationItem[];
  metaTitleTranslations: TranslationItem[];
  metaDescriptionTranslations: TranslationItem[];
}

export interface MunicipalityListItem {
  id: string;
  name: string;
}

export interface MunicipalitiesFilter extends PaginationParameters {
  searchString: string;
}

export interface MunicipalitiesState {
  municipalityItems: MunicipalityListItem[];
  municipalityDetails: MunicipalityDetails;
  municipalityItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  municipalityDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  municipalityPagination: PaginationResponse;
}

const initialState: MunicipalitiesState = {
  municipalityItems: [],
  municipalityDetails: {
    id: '',
    isDeleted: false,
    regionId: '',
    latitude: '',
    longitude: '',
    nameTranslations: [],
    descriptionTranslations: [],
    metaTitleTranslations: [],
    metaDescriptionTranslations: [],
  },
  municipalityItemsStatus: LOADING_STATUS.IDLE,
  municipalityDetailsStatus: LOADING_STATUS.IDLE,
  municipalityPagination: InitialPaginationResponse,
};

export const getMunicipalitiesAsync = createAsyncThunk(
  'municipalities/getMunicipalities',
  async (municipalitiesFilter: MunicipalitiesFilter, { rejectWithValue }) => {
    try {
      const response = await fetchMunicipalities(municipalitiesFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getMunicipalityDetailsAsync = createAsyncThunk(
  'municipalities/getMunicipalityDetails',
  async (municipalityId: string, { rejectWithValue }) => {
    try {
      const response = await fetchMunicipalityDetails(municipalityId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveMunicipalityAsync = createAsyncThunk(
  'municipalities/addMunicipality',
  async (municipalityData: MunicipalityDetails, { rejectWithValue }) => {
    try {
      const response = await addMunicipality(municipalityData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateMunicipalityAsync = createAsyncThunk(
  'municipalities/updateMunicipality',
  async (municipalityData: MunicipalityDetails, { rejectWithValue }) => {
    try {
      const response = await updateMunicipality(municipalityData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteMunicipalityAsync = createAsyncThunk(
  'municipalities/deleteMunicipality',
  async (municipalityId: string, { rejectWithValue }) => {
    try {
      const response = await deleteMunicipality(municipalityId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const municipalitiesSlice = createSlice({
  name: 'municipalities',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMunicipalitiesAsync.pending, state => {
        state.municipalityItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getMunicipalitiesAsync.fulfilled, (state, action) => {
        state.municipalityItemsStatus = LOADING_STATUS.IDLE;
        state.municipalityItems = action.payload.data;
        state.municipalityPagination = PopulatePagination(action.payload);
      })
      .addCase(getMunicipalitiesAsync.rejected, state => {
        state.municipalityItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getMunicipalityDetailsAsync.pending, state => {
        state.municipalityDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getMunicipalityDetailsAsync.fulfilled, (state, action) => {
        state.municipalityDetailsStatus = LOADING_STATUS.IDLE;
        state.municipalityDetails = action.payload;
      })
      .addCase(getMunicipalityDetailsAsync.rejected, state => {
        state.municipalityDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectMunicipalityItems = (state: RootState) =>
  state.municipalities.municipalityItems;
export const selectMunicipalityPagination = (state: RootState) =>
  state.municipalities.municipalityPagination;
export const selectMunicipalityItemsLoadingStatus = (state: RootState) =>
  state.municipalities.municipalityItemsStatus;
export const selectMunicipalityDetails = (state: RootState) =>
  state.municipalities.municipalityDetails;
export const selectMunicipalityDetailsLoadingStatus = (state: RootState) =>
  state.municipalities.municipalityDetailsStatus;

export default municipalitiesSlice.reducer;
